import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Search, FileText, Clock, Eye, CheckCircle2, XCircle, User, Mail, Calendar, MapPin, Briefcase, Download, Filter, RefreshCw, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api-client';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { normalizeMediaUrl } from '../../utils/media';

interface ApplicationsManagementProps {
  applications: any[];
  onApplicationUpdate: (applications: any[]) => void;
}

export function ApplicationsManagement({ applications: initialApplications, onApplicationUpdate }: ApplicationsManagementProps) {
  const [applications, setApplications] = useState<any[]>(initialApplications || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [jobFilter, setJobFilter] = useState<string>('all');
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load applications from backend
  useEffect(() => {
    loadApplications();
  }, [filterStatus]);

  const loadApplications = async () => {
    setIsLoading(true);
    try {
      const params: any = { limit: 500 };
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      const res = await apiClient.getAdminApplications(params);
      const items = Array.isArray(res?.applications) ? res.applications : [];
      const mapped = items.map((app: any) => ({
        id: app._id || app.id,
        jobId: app.job_id || app.jobId,
        userId: app.job_seeker_id || app.userId,
        jobTitle: app.job?.title || app.jobTitle || 'N/A',
        company: app.job?.company || app.company || 'N/A',
        applicantName: app.user?.full_name || app.user?.name || app.applicantName || 'Unknown',
        applicantEmail: app.user?.email || app.applicantEmail || 'N/A',
        status: app.status || 'pending',
        appliedAt: app.applied_at || app.appliedAt || new Date().toISOString(),
        coverLetter: app.cover_letter || app.coverLetter,
        resumeUrl: app.resume_url || app.resumeUrl,
      }));
      setApplications(mapped);
      onApplicationUpdate(mapped);
    } catch (error) {
      console.error('Failed to load applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch =
      app.applicantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.applicantEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.company?.toLowerCase().includes(searchTerm.toLowerCase());

    // Improved status matching logic
    let matchesStatus = true;
    if (filterStatus === 'all') {
      matchesStatus = true;
    } else if (filterStatus === 'pending') {
      matchesStatus = app.status === 'pending';
    } else if (filterStatus === 'reviewed') {
      matchesStatus = app.status === 'reviewed' || app.status === 'shortlisted';
    } else if (filterStatus === 'accepted') {
      matchesStatus = app.status === 'accepted' || app.status === 'hired';
    } else if (filterStatus === 'rejected') {
      matchesStatus = app.status === 'rejected';
    } else {
      matchesStatus = app.status === filterStatus;
    }

    const matchesJob = jobFilter === 'all' || String(app.jobId || app.jobTitle) === jobFilter;
    return matchesSearch && matchesStatus && matchesJob;
  });

  console.log('📊 Admin Applications Filter:', {
    total: applications.length,
    filtered: filteredApplications.length,
    filterStatus,
    searchTerm
  });

  const handleStatusUpdate = async (applicationId: string, newStatus: 'pending' | 'reviewed' | 'accepted' | 'rejected') => {
    try {
      await apiClient.updateApplicationStatus(applicationId, newStatus);
      
      // Update local state
      const updated = applications.map(app =>
        app.id === applicationId ? { ...app, status: newStatus } : app
      );
      setApplications(updated);
      onApplicationUpdate(updated);
      
      toast.success(`Application status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update application status');
    }
  };

  const handleViewDetails = (application: any) => {
    setSelectedApplication(application);
    setShowDetailsDialog(true);
  };

  const getStatusBadge = (status: string) => {
    const config: any = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, label: 'Pending' },
      reviewed: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Eye, label: 'Reviewed' },
      accepted: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2, label: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'Rejected' }
    };
    return config[status] || config.pending;
  };

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    accepted: applications.filter(a => a.status === 'accepted').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
              <FileText className="h-8 w-8 text-white" />
            </div>
            Applications Management
          </h2>
          <p className="text-gray-500 mt-1">Review and manage job applications</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            className="gap-2" 
            onClick={() => {
              loadApplications();
              toast.success('Refreshing applications...');
            }} 
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => {
              // Export applications to CSV
              const csvContent = [
                ['Applicant Name', 'Email', 'Job Title', 'Company', 'Status', 'Applied Date'].join(','),
                ...filteredApplications.map(app => [
                  app.applicantName,
                  app.applicantEmail,
                  app.jobTitle,
                  app.company,
                  app.status,
                  new Date(app.appliedAt).toLocaleDateString()
                ].join(','))
              ].join('\n');
              
              const blob = new Blob([csvContent], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `applications_${filterStatus}_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
              toast.success(`Exported ${filteredApplications.length} applications`);
            }}
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards - Clickable for filtering */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card 
          className={`border-l-4 border-l-gray-500 cursor-pointer transition-all hover:shadow-lg ${filterStatus === 'all' ? 'ring-2 ring-gray-500' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-l-4 border-l-yellow-500 cursor-pointer transition-all hover:shadow-lg ${filterStatus === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setFilterStatus('pending')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-l-4 border-l-blue-500 cursor-pointer transition-all hover:shadow-lg ${filterStatus === 'reviewed' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setFilterStatus('reviewed')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Reviewed</p>
                <p className="text-2xl font-bold text-gray-900">{stats.reviewed}</p>
              </div>
              <Eye className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-l-4 border-l-green-500 cursor-pointer transition-all hover:shadow-lg ${filterStatus === 'accepted' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setFilterStatus('accepted')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Accepted</p>
                <p className="text-2xl font-bold text-gray-900">{stats.accepted}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-l-4 border-l-red-500 cursor-pointer transition-all hover:shadow-lg ${filterStatus === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFilterStatus('rejected')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Rejected</p>
                <p className="text-2xl font-bold text-gray-900">{stats.rejected}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search by applicant name, email, job title, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-11 h-11"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={jobFilter}
                onChange={(e) => setJobFilter(e.target.value)}
                className="px-3 py-2 bg-white border rounded h-11 text-sm"
              >
                <option value="all">All Jobs</option>
                {Array.from(new Map(applications.map(a => [String(a.jobId || a.jobTitle), { id: String(a.jobId || a.jobTitle), label: `${a.jobTitle} • ${a.company}` }])).values()).map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[180px] h-11">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      <Card>
        <CardHeader className="border-b border-gray-200 bg-gray-50">
          <CardTitle className="text-lg font-semibold">
            Applications ({filteredApplications.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {filteredApplications.map((application) => {
              const statusConfig = getStatusBadge(application.status);
              const StatusIcon = statusConfig.icon;

              return (
                <div
                  key={application.id}
                  className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-all duration-200 bg-white"
                >
                  <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                    {/* Application Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl">
                          {application.applicantName.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div>
                              <h3 className="font-semibold text-lg text-gray-900">{application.applicantName}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Mail className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-600">{application.applicantEmail}</span>
                              </div>
                            </div>
                            <Badge className={`${statusConfig.color} border`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>

                          {/* Job Details */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-sm">
                            <div className="flex items-center gap-2">
                              <Briefcase className="h-4 w-4 text-gray-400" />
                              <div>
                                <p className="text-xs text-gray-500">Position</p>
                                <p className="font-medium text-gray-900">{application.jobTitle}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-gray-400" />
                              <div>
                                <p className="text-xs text-gray-500">Company</p>
                                <p className="font-medium text-gray-900">{application.company}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <div>
                                <p className="text-xs text-gray-500">Applied</p>
                                <p className="font-medium text-gray-900">
                                  {new Date(application.appliedAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap lg:flex-col gap-2">
                      <Button
                        onClick={() => handleViewDetails(application)}
                        variant="outline"
                        size="sm"
                        className="gap-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                      {application.resumeUrl && (
                        <Button asChild variant="outline" size="sm" className="gap-2">
                          <a href={application.resumeUrl} target="_blank" rel="noopener noreferrer">
                            <Download className="h-4 w-4" />
                            Resume
                          </a>
                        </Button>
                      )}
                      {application.status === 'pending' && (
                        <>
                          <Button
                            onClick={() => handleStatusUpdate(application.id, 'reviewed')}
                            variant="outline"
                            size="sm"
                            className="gap-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                          >
                            <Eye className="h-4 w-4" />
                            Review
                          </Button>
                          <Button
                            onClick={() => handleStatusUpdate(application.id, 'accepted')}
                            variant="outline"
                            size="sm"
                            className="gap-2 hover:bg-green-50 hover:text-green-600 hover:border-green-300"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Accept
                          </Button>
                          <Button
                            onClick={() => handleStatusUpdate(application.id, 'rejected')}
                            variant="outline"
                            size="sm"
                            className="gap-2 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                          >
                            <XCircle className="h-4 w-4" />
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredApplications.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Application Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
            <DialogDescription>View complete application information</DialogDescription>
          </DialogHeader>
          {selectedApplication && (
            <div className="space-y-6">
              {/* Applicant Info */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-lg mb-4">Applicant Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{selectedApplication.applicantName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedApplication.applicantEmail}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Applied Date</p>
                    <p className="font-medium">
                      {new Date(selectedApplication.appliedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <Badge className={getStatusBadge(selectedApplication.status).color}>
                      {getStatusBadge(selectedApplication.status).label}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Job Info */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Job Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Position</p>
                    <p className="font-medium">{selectedApplication.jobTitle}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Company</p>
                    <p className="font-medium">{selectedApplication.company}</p>
                  </div>
                </div>
              </div>

              {/* Cover Letter */}
              {selectedApplication.coverLetter && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Cover Letter</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedApplication.coverLetter}
                    </p>
                  </div>
                </div>
              )}

              {/* Resume */}
              {selectedApplication.resumeUrl && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Resume</h3>
                  <Button variant="outline" className="gap-2" asChild>
                    <a href={selectedApplication.resumeUrl} target="_blank" rel="noopener noreferrer">
                      <Download className="h-4 w-4" />
                      Download Resume
                    </a>
                  </Button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => {
                    handleStatusUpdate(selectedApplication.id, 'accepted');
                    setShowDetailsDialog(false);
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Accept Application
                </Button>
                <Button
                  onClick={() => {
                    handleStatusUpdate(selectedApplication.id, 'rejected');
                    setShowDetailsDialog(false);
                  }}
                  variant="outline"
                  className="flex-1 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject Application
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
